// Shared types for Canyon Storm plan variants (Rulebringers, Dawnbreakers, …).
// Each variant is a full CSPlanConfig object; the registry in ./index.ts binds
// human-readable keys ("rulebringers") to concrete configs.

export type Priority = 'critical' | 'high' | 'medium' | 'lower';

export type PlanSlotKey =
  | 'dataCenter1'
  | 'dataCenter2'
  | 'powerTower'
  | 'serumFactory1'
  | 'serumFactory2'
  | 'defenseSystem1'
  | 'defenseSystem2'
  | 'virusLab'
  | 'warehouse1'
  | 'warehouse2'
  | 'warehouse3'
  | 'warehouse4';

export type NonVirusSlot = Exclude<PlanSlotKey, 'virusLab'>;

export const NON_VIRUS_SLOTS: NonVirusSlot[] = [
  'dataCenter1',
  'dataCenter2',
  'powerTower',
  'serumFactory1',
  'serumFactory2',
  'defenseSystem1',
  'defenseSystem2',
  'warehouse1',
  'warehouse2',
  'warehouse3',
  'warehouse4',
];

export type PhaseDef = {
  title: string;
  time: string;
  timeColor: string;
  subtitle: string;
  subtitleColor: string;
  priorities: Record<PlanSlotKey, Priority | null>;
  /**
   * Absolute player count per slot in this phase, including Virus Lab.
   * Sum across all slots should equal the intended roster size (e.g. 20).
   */
  capacities: Record<PlanSlotKey, number>;
  /**
   * Ordered strength claims for this phase. Each entry pulls the top-N
   * starters (by M1 power) into the given slot, in list order, before the
   * home-based fill runs for other slots. Claimed players are removed from
   * every home list so they can't be double-placed.
   *
   * Use for "collapse"-style phases where specific buildings should always
   * get the strongest remaining players (e.g. Rulebringers P4: Virus takes
   * the top 6, Power Tower takes the next 4).
   *
   * Claim count for a slot should equal that slot's capacity in this phase.
   */
  strengthClaims?: { slot: PlanSlotKey; count: number }[];
};

export type VariantMeta = {
  key: string;
  name: string;
  shortDescription: string;
  integratedAt: string; // ISO date
  rulebookRef: string;
  highlights: string[];
  /** true = rules not fully specified yet; UI may hide or mark the variant. */
  draft?: boolean;
};

export type CSPlanConfig = {
  meta: VariantMeta;
  phases: PhaseDef[];
  /**
   * Home-slot allocation by strength rank. Index 0 = strongest non-flex starter
   * (M1 power). The entry at each index says which building that starter
   * belongs to as their "home" throughout the match.
   */
  homeByRank: NonVirusSlot[];
  /**
   * Ranks that make up the "PT core" — these players never receive a sub
   * pairing. Same indexing as homeByRank.
   */
  ptCoreRanks: readonly number[];
};
